import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Check, ShoppingCart, Mail, Copy, Rocket, BarChart3, TrendingUp, Mail as MailIcon, Target, Zap, Plug, Palette, Lock } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "@/components/Footer";
import ParticleBurst from "@/components/ParticleBurst";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strip the leading emoji from "📊 Basic Dashboard" → "Basic Dashboard"
// and pick a matching lucide-react icon based on keywords in the label.
const stripEmoji = (s = "") =>
  s.replace(/^\s*\p{Extended_Pictographic}+\s*/u, "").trim();

const pickFeatureIcon = (label) => {
  const t = label.toLowerCase();
  if (t.includes("api")) return Plug;
  if (t.includes("brand")) return Palette;
  if (t.includes("security") || t.includes("encrypt")) return Lock;
  if (t.includes("priority") || t.includes("speed") || t.includes("fast")) return Zap;
  if (t.includes("advanced report") || t.includes("report")) return TrendingUp;
  if (t.includes("dashboard") && t.includes("full")) return Target;
  if (t.includes("dashboard")) return BarChart3;
  if (t.includes("email") || t.includes("support")) return MailIcon;
  return Check;
};

const PurchasePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [email, setEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  // Account-ready state shown after a successful free trial signup
  const [accountReady, setAccountReady] = useState(null); // { email, temp_password, login_url, tier }
  const [showBurst, setShowBurst] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  // Auto-select tier ONCE on initial load if ?tier=free|pro is provided.
  // We use a ref so subsequent state changes (e.g. clicking "Back to Plans")
  // never re-trigger the auto-select.
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (pricing.length === 0) return;
    const requestedTier = searchParams.get("tier");
    if (requestedTier) {
      const match = pricing.find((t) => t.tier === requestedTier);
      if (match) {
        setSelectedTier(match);
        setShowEmailForm(true);
        didAutoSelectRef.current = true;
      }
    }
  }, [pricing, searchParams]);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricing(response.data.tiers);
    } catch (error) {
      toast.error("Failed to load pricing");
      console.error(error);
    }
  };

  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    setShowEmailForm(true);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode || !selectedTier) return;

    try {
      setValidatingCoupon(true);
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode,
        tier: selectedTier.tier
      });

      setCouponData(response.data);
      toast.success(`Coupon applied! You save $${response.data.discount_amount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid coupon code");
      setCouponData(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setCouponData(null);
  };

  const handleFreeTrial = async () => {
    // Clear any previous toast notifications to prevent stale messages
    toast.dismiss();
    
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/purchase/free-trial`, {
        user_email: email,
        tier: "free"
      });

      if (response.data.success && response.data.temp_password) {
        toast.success("✅ Your Maidly account is ready!");
        setAccountReady({
          email: response.data.email,
          temp_password: response.data.temp_password,
          login_url: response.data.login_url,
          tier: "free",
        });
        setShowBurst(true);
        setShowEmailForm(false);
        setEmail("");
      } else {
        toast.error(response.data.message || "Failed to activate trial");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get free trial");
    } finally {
      setLoading(false);
    }
  };

  const handleProPurchase = async () => {
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    try {
      setLoading(true);
      // Store coupon for use after PayPal redirect
      if (couponData) {
        localStorage.setItem("pending_coupon", couponCode);
      }
      
      const response = await axios.post(`${API}/purchase/create-order`, {
        user_email: email,
        tier: "pro"
      });

      // Redirect to PayPal
      window.location.href = response.data.approval_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment order");
      setLoading(false);
    }
  };

  const handlePurchase = () => {
    if (selectedTier?.tier === "free") {
      handleFreeTrial();
    } else if (selectedTier?.tier === "pro") {
      handleProPurchase();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {showBurst && (
        <ParticleBurst
          count={120}
          duration={2400}
          onDone={() => setShowBurst(false)}
        />
      )}      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Maidly Business
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <button
              data-testid="back-to-home-btn"
              onClick={() => navigate("/")}
              className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-block"
            >
              ← Back to Home
            </button>
          </div>

        {accountReady ? (
          /* Account Ready View — Option A: Instant SaaS Access */
          <div className="max-w-2xl mx-auto">
            <Card className="bg-white border border-gray-200 shadow-md">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#22c55e' }}>
                  <Check className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-2 text-gray-800">
                  Your Account is Ready
                </h2>
                <p className="text-gray-600 mb-6">
                  Your <strong>{accountReady.tier === 'free' ? '15-day free trial' : 'Pro'}</strong> Maidly Business account has been created. Use the credentials below to log in.
                </p>

                <div className="bg-amber-50 border border-amber-200 p-5 mb-6 text-left">
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-amber-800 mb-1 tracking-wider">EMAIL</div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-gray-900 break-all">{accountReady.email}</span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(accountReady.email); toast.success('Email copied'); }}
                        className="text-amber-700 hover:text-amber-900 flex-shrink-0"
                        title="Copy email"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-amber-800 mb-1 tracking-wider">TEMPORARY PASSWORD</div>
                    <div className="flex items-center justify-between gap-2 bg-white border border-dashed border-amber-300 p-3">
                      <span className="font-mono text-gray-900 break-all">{accountReady.temp_password}</span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(accountReady.temp_password); toast.success('Password copied'); }}
                        className="text-amber-700 hover:text-amber-900 flex-shrink-0"
                        title="Copy password"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-amber-800 mt-2">⚠️ Please change this password after your first login (Settings → Security).</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 p-3 mb-6 flex items-center gap-3">
                  <Mail className="w-5 h-5 flex-shrink-0" style={{ color: '#0073ea' }} />
                  <p className="text-sm text-blue-800 text-left">
                    A copy of these credentials has been emailed to <strong>{accountReady.email}</strong>.
                  </p>
                </div>

                <button
                  data-testid="launch-app-btn"
                  onClick={() => navigate(`/login?email=${encodeURIComponent(accountReady.email)}&from=trial`)}
                  className="w-full py-4 text-white font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#0073ea' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#005bb5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0073ea'}
                >
                  <Rocket className="w-5 h-5" />
                  Launch App
                </button>

                <button
                  onClick={() => { setAccountReady(null); navigate('/'); }}
                  className="mt-3 text-sm text-gray-600 hover:text-gray-900"
                >
                  ← Back to Home
                </button>
              </CardContent>
            </Card>
          </div>
        ) : !showEmailForm ? (
          /* Pricing Cards */
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricing.map((tier, index) => (
              <Card
                key={tier.tier}
                data-testid={`pricing-card-${tier.tier}`}
                className={`bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow relative ${
                  tier.tier === "pro" ? "ring-2" : ""
                }`}
                style={tier.tier === "pro" ? { borderColor: '#0073ea' } : {}}
              >
                {tier.tier === "pro" && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 text-white text-sm font-semibold" style={{ backgroundColor: '#0073ea' }}>Most Popular</span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-center">
                    <div
                      className="p-6 text-white mb-4 rounded-lg"
                      style={{
                        background:
                          tier.tier === "free"
                            ? "linear-gradient(135deg, #0073ea 0%, #005bb5 100%)"
                            : "linear-gradient(135deg, #0073ea 0%, #0b2545 100%)",
                      }}
                    >
                      <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                      <div className="text-4xl font-bold mb-1">
                        ${tier.price}
                        {tier.price > 0 && <span className="text-lg"> /year</span>}
                      </div>
                      <p className="text-sm opacity-90">{tier.duration_text}</p>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2.5 mb-6">
                    {tier.features.map((feature) => {
                      const label = stripEmoji(feature);
                      const Icon = pickFeatureIcon(label);
                      return (
                        <li
                          key={feature}
                          className="flex items-center gap-3 text-sm text-gray-800 py-1.5 px-2 rounded-md transition-colors hover:bg-blue-50"
                        >
                          <span
                            className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
                            style={{
                              background: "rgba(0,115,234,0.10)",
                              color: "#0073ea",
                            }}
                          >
                            <Icon className="w-4 h-4" />
                          </span>
                          <span className="font-medium">{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <Button
                    data-testid={`select-${tier.tier}-btn`}
                    onClick={() => handleTierSelect(tier)}
                    className="w-full py-6 text-lg text-white font-semibold transition-all duration-200"
                    style={{ backgroundColor: '#0073ea' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#005bb5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#0073ea'}
                    onMouseDown={(e) => e.target.style.backgroundColor = '#C25A10'}
                    onMouseUp={(e) => e.target.style.backgroundColor = '#005bb5'}
                  >
                    {tier.price === 0 ? "Start Free Trial" : "Purchase Now"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Email Form */
          <Card className="bg-white border border-gray-200 shadow-md max-w-xl mx-auto">
            <CardHeader>
              <CardTitle className="text-center">
                <div
                  className="p-6 text-white mb-4 rounded-lg"
                  style={{
                    background:
                      selectedTier?.tier === "free"
                        ? "linear-gradient(135deg, #0073ea 0%, #005bb5 100%)"
                        : "linear-gradient(135deg, #0073ea 0%, #0b2545 100%)",
                  }}
                >
                  <h3 className="text-2xl font-bold mb-2">{selectedTier?.name}</h3>
                  <div className="text-4xl font-bold">
                    ${selectedTier?.price}
                    {selectedTier?.price > 0 && <span className="text-lg"> /year</span>}
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  data-testid="purchase-email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 focus:border-blue-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Your login credentials will be sent to this email address
                </p>
              </div>

              {/* Coupon Code Section - Only for Pro tier */}
              {selectedTier?.tier === "pro" && (
                <div className="border-2 border-dashed border-purple-200 rounded-lg p-4 bg-purple-50">
                  <Label className="text-sm font-semibold text-purple-900 flex items-center gap-2 mb-2">
                    🎫 Have a discount code?
                  </Label>
                  
                  {!couponData ? (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1 border-purple-300"
                      />
                      <Button
                        onClick={handleApplyCoupon}
                        disabled={!couponCode || validatingCoupon}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {validatingCoupon ? "Checking..." : "Apply"}
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-green-900">
                            ✓ Coupon "{couponData?.coupon?.code || 'N/A'}" applied!
                          </p>
                          <p className="text-sm text-green-700 mt-1">
                            Original: ${couponData?.original_price || 0} → 
                            <span className="font-bold text-green-800"> ${couponData?.discounted_price?.toFixed(2) || '0.00'}</span>
                            <span className="text-green-600"> (Save ${couponData?.discount_amount?.toFixed(2) || '0.00'})</span>
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveCoupon}
                          className="text-red-600"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  {selectedTier?.tier === "free" ? (
                    <>
                      <li>✓ Your Maidly account is created instantly</li>
                      <li>✓ Login credentials emailed to you</li>
                      <li>✓ Click "Launch App" to sign in immediately</li>
                      <li>✓ Full access for 15 days — no download required</li>
                    </>
                  ) : (
                    <>
                      <li>✓ Complete payment via PayPal</li>
                      <li>✓ Your Maidly account is activated for 1 year</li>
                      <li>✓ Login credentials & license key emailed to you</li>
                      <li>✓ Click "Launch App" to sign in immediately</li>
                    </>
                  )}
                </ul>
              </div>

              <div className="flex gap-3">
                <Button
                  data-testid="back-to-plans-btn"
                  variant="outline"
                  onClick={() => {
                    // Clear ?tier=... from the URL so the auto-select effect
                    // doesn't immediately re-open the email form.
                    setSearchParams({});
                    setShowEmailForm(false);
                    setSelectedTier(null);
                    setEmail("");
                  }}
                  className="flex-1 py-6 text-lg rounded-lg"
                >
                  Back to Plans
                </Button>
                <Button
                  data-testid="confirm-purchase-btn"
                  onClick={handlePurchase}
                  disabled={loading || !email}
                  className={`flex-1 py-6 text-lg rounded-lg shadow-lg ${
                    selectedTier?.tier === "free"
                      ? "bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                      : "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                  } text-white`}
                >
                  {loading
                    ? "Processing..."
                    : selectedTier?.tier === "free"
                    ? "Start Free Trial"
                    : "Proceed to Payment"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trust Badges */}
        <div className="mt-12 text-center animate-fade-in">
          <p className="text-sm text-gray-500 mb-4">Trusted by businesses worldwide</p>
          <div className="flex justify-center items-center gap-8 flex-wrap">
            <div className="flex items-center gap-2 text-gray-600">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-sm">Secure Payment</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-sm">Instant Activation</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-sm">24/7 Support</span>
            </div>
          </div>
        </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PurchasePage;